import { call, put, takeEvery, takeLatest } from "redux-saga/effects"
import { updateToken } from "../../utils/updateToken"
import {
  householdsActions,
  actionTypes,
} from "../store/reducers/householdsReducer"
import { householdsAPI } from "../api/api"
import {SagaWrapper} from "./index";
import { generalActions } from "../store/reducers/generalReducer";
import { isOnline } from "../../offline/isOnline";
import { isOfflineFeatureEnabled } from "../../offline/featureFlag";
import { enqueue } from "../../offline/queue";
import { newIdempotencyKey } from "../../offline/idempotency";
import { refreshOfflineCounters } from "./routesSaga";
import i18n from "../../res/languages";

// `: any` return type (matches updateHousehold below): without it, TS infers a
// precise Generator type from the yielded effects that redux-saga's `Saga<any[]>`
// interface (used by `runSaga` in tests exercising this generator directly) can't
// accept — the resume-value type of `next()` ends up too narrow.
export function* getHouseholds({payload}: any): any {
  try {
    yield put(householdsActions.toggleIsLoading(true))
    const response: { status: number; data: { data: {} } } =
      yield householdsAPI.getHouseholds({ ...payload })
    if (response.status === 200) {
      updateToken()
      yield put(householdsActions.getHouseholdsAC(response.data))
      yield put(householdsActions.toggleIsLoading(false))
    }
  } catch (error) {
    console.log(error)
    yield put(householdsActions.toggleIsLoading(false))
  }
}

function* getHouseholdsDownload({payload}: any) {
  try {
    yield put(householdsActions.toggleIsLoadingDownload(true))
    const response: { status: number; data: { data: {} } } =
      yield householdsAPI.getHouseholdsDownload({ ...payload })
    if (response.status === 200) {
      updateToken()
      yield put(householdsActions.getHouseholdsDownloadAC(response.data.data))
      yield put(householdsActions.toggleIsLoadingDownload(false))
    }
  } catch (error) {
    console.log(error)
    yield put(householdsActions.toggleIsLoadingDownload(false))
  }
}

function* getHouseholdCategories(payload: any) {
  try {
    const response: { status: number; data: { data: {} } } =
        yield householdsAPI.getHouseholdCategories()
    if (response.status === 200) {
      yield put(householdsActions.getHouseholdCategories(response.data))
    }
  } catch (error) {
    console.log(error)
  }
}

function* createRegularCategory(data: any) {
  try {
    const response: { data: null, status: number } = yield householdsAPI.createRegularCategory(data.name);
    if(response.status === 201) {
      yield getHouseholdCategories(null)
    }
  } catch (e) {
    console.error(e);
  }
}

function* deleteRegularCategory(data: any) {
  try {
    yield householdsAPI.deleteRegularCategory(data.id);
    yield getHouseholdCategories(null)
  } catch (e) {
    console.error(e);
  }
}

function* updateRegularCategory(data: any) {
  try {
    yield householdsAPI.updateRegularCategory(data.id, data.name);
    yield getHouseholdCategories(null)
  } catch (e) {
    console.error(e);
  }
}

function* deleteHouseholdsByIds({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "delete",
      ids: action.payload.data.ids,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.DELETE_HOUSEHOLDS_BY_IDS_SUCCESS,
      actionTypes.DELETE_HOUSEHOLDS_BY_IDS_ERROR,
  )
}

function* deleteAllHouseholdsExcludeIds({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "delete",
      all: true,
      excluded_ids: action.payload.data.excluded_ids,
      filters: action.payload.data.filters,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.DELETE_ALL_HOUSEHOLDS_EXCLUDE_IDS_SUCCESS,
      actionTypes.DELETE_ALL_HOUSEHOLDS_EXCLUDE_IDS_ERROR,
  )
}

function* removeRegularCategoriesForHouseholdsByIds({ request, action, dispatchResponse }: any) {
  // detach on the backend (HouseholdService::bulkRemoveRegularCategories): takes
  // OFF only the named categories and leaves every other category on the
  // household alone. Deliberately not expressed as "update with a shorter list"
  // — that is update_regular_categories, whose sync() replaces the whole set and
  // strips anything not re-listed. A household that never carried the category
  // is simply unaffected, so a mixed selection needs no special handling here.
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "remove_regular_categories",
      ids: action.payload.data.ids,
      category_ids: action.payload.data.category_ids,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS_SUCCESS,
      actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS_ERROR,
  )
}

function* updateRegularCategoriesForHouseholdsByIds({ request, action, dispatchResponse }: any) {
  // Additive (add_regular_categories = syncWithoutDetaching), not "update_regular_categories"
  // (a full sync/replace) — the latter silently stripped any categories the selected
  // households already had that weren't re-checked in the bulk dialog.
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "add_regular_categories",
      ids: action.payload.data.ids,
      category_ids: action.payload.data.category_ids,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS_SUCCESS,
      actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS_ERROR,
  )
}

function* removeRegularCategoriesForAllHouseholdsExcludeIds({ request, action, dispatchResponse }: any) {
  // Same detach as the by-ids form, over the set the backend resolves from the
  // list's own filter state (bulkActions resolves `all` before dispatching the
  // action, so nothing about removal has to be special-cased there).
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "remove_regular_categories",
      all: true,
      excluded_ids: action.payload.data.excluded_ids,
      category_ids: action.payload.data.category_ids,
      filters: action.payload.data.filters,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_SUCCESS,
      actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_ERROR,
  )
}

function* updateRegularCategoriesForAllHouseholdsExcludeIds({ request, action, dispatchResponse }: any) {
  // See note above — additive, not a destructive replace.
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "add_regular_categories",
      all: true,
      excluded_ids: action.payload.data.excluded_ids,
      category_ids: action.payload.data.category_ids,
      filters: action.payload.data.filters,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_SUCCESS,
      actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_ERROR,
  )
}

function* updateSchedulingForHouseholdsByIds({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "update_periodicity",
      ids: action.payload.data.ids,
      periodicity: action.payload.data.periodicity,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.UPDATE_SCHEDULING_FOR_HOUSEHOLDS_BY_IDS_SUCCESS,
      actionTypes.UPDATE_SCHEDULING_FOR_HOUSEHOLDS_BY_IDS_ERROR,
  )
}

function* updateSchedulingForAllHouseholdsExcludeIds({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households/bulk-actions',
    body: {
      action: "update_periodicity",
      all: true,
      excluded_ids: action.payload.data.excluded_ids,
      periodicity: action.payload.data.periodicity,
      filters: action.payload.data.filters,
    }
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.UPDATE_SCHEDULING_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_SUCCESS,
      actionTypes.UPDATE_SCHEDULING_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS_ERROR,
  )
}

export function* getHousehold(payload: any) {
  try {
    const response: { status: number; data: { data: {} } } =
      yield householdsAPI.getHouseholdsById(payload.id)
    if (response.status === 200) {
      updateToken()
      yield put(householdsActions.getHouseholdAC(response.data.data))
    }
  } catch (error) {
    // Offline this is a cache miss on a per-household URL, for any contact whose
    // card was never opened online — so serve the copy the prefetch downloaded.
    // Deliberately NOT done when online: there a failure is a real server error,
    // and quietly rendering a downloaded copy would hide it.
    if (!isOnline() && isOfflineFeatureEnabled()) {
      yield put(householdsActions.useOfflineHouseholdAC(payload.id))
      return
    }
    console.log(error)
  }
}

function* exportHouseholds({ request, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "get",
    endpoint: "/api/auth/households/export-as-download-file"
  })

  yield dispatchResponse(
      { data, error },
      actionTypes.EXPORT_HOUSEHOLDS_SUCCESS,
      actionTypes.EXPORT_HOUSEHOLDS_ERROR,
  )
}

function* importHouseholds({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: "/api/auth/households/import",
    body: action.payload.data
  })

  yield dispatchResponse(
      { data, error },
      actionTypes.IMPORT_HOUSEHOLDS_SUCCESS,
      actionTypes.IMPORT_HOUSEHOLDS_ERROR,
  )
}

function* importPreview(action: any): Generator<any, void, any> {
  try {
    const response: any = yield call([householdsAPI, householdsAPI.importPreview], action.payload.file);
    if (response && (response.status === 200 || response.status === 201)) {
      yield put({
        type: actionTypes.IMPORT_PREVIEW_SUCCESS,
        payload: {
          headers: response.data.headers || [],
          rows: response.data.rows || [],
          row_count: response.data.row_count || 0,
        },
      });
    }
  } catch (error: any) {
    yield put({ type: actionTypes.IMPORT_PREVIEW_ERROR });
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "Failed to read file preview",
        type: "error",
      })
    );
  }
}

const ADDRESS_KEYS = [
  "addresses",
  "homeAddress",
  "address",
  "place_id",
  "latitude",
  "longitude",
  "is_manual",
];

/**
 * Fallback test for callers that submit a targeted payload and cannot say what
 * changed — the contact list's `{ periodicity }` and `{ categories }` updates.
 * There, the presence of an address-shaped key IS the edit.
 *
 * A caller that submits the whole household on every save (the edit form) must
 * send `addressesTouched` instead: for it this test is always false, and a phone
 * correction gets refused for carrying addresses it never touched. See
 * `offline/addressChange.ts`.
 */
export const isQueueableContactEdit = (data: any): boolean => {
  const touchesAddress = (value: any): boolean => {
    if (!value || typeof value !== "object") return false;
    if (Array.isArray(value)) return value.some(touchesAddress);
    return Object.keys(value).some(
      (key) => ADDRESS_KEYS.includes(key) || touchesAddress(value[key])
    );
  };
  return !touchesAddress(data);
};

export function* updateHousehold ({ request, action, dispatchResponse }: any): any {
  const { id, data: body, addressesTouched } = action.payload;

  if (!isOnline() && isOfflineFeatureEnabled()) {
    // A caller that knows what its user changed says so; one that doesn't gets the
    // conservative payload scan. Never the other way round: the scan cannot see that
    // the whole-household payload from the edit form left every address alone.
    const touchesAddress =
      addressesTouched === undefined ? !isQueueableContactEdit(body) : Boolean(addressesTouched);

    if (touchesAddress) {
      yield put(
        generalActions.errorAC({
          message: i18n.t("offline.address_edit_needs_connection"),
          type: "error",
        })
      );
      return;
    }

    yield call(enqueue, {
      type: "UPDATE_HOUSEHOLD_CONTACT",
      entityKey: `household:${id}`,
      payload: { id, data: body },
      idempotencyKey: newIdempotencyKey(),
    });
    yield call(refreshOfflineCounters);

    // Confirm, exactly as the visit-report queue does. Without this the edit form's
    // modal never closes and its caller's in-flight latch never resets, so a queued
    // save is indistinguishable from one that silently failed — and the volunteer
    // retypes an edit that is already saved.
    const onSuccess = action.callbacks?.onSuccess ?? action.payload?.callbacks?.onSuccess;
    if (typeof onSuccess === "function") {
      yield call(onSuccess, null);
    }
    return;
  }

  const { data, error } = yield request({
    method: 'put',
    endpoint: '/api/auth/households/' + action.payload.id,
    body: action.payload.data,
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.UPDATE_HOUSEHOLD_SUCCESS,
      actionTypes.UPDATE_HOUSEHOLD_ERROR,
  );
}

function* createHousehold({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "post",
    endpoint: '/api/auth/households',
    body: action.payload.data
  });

  yield dispatchResponse(
      { data, error },
      actionTypes.CREATE_HOUSEHOLD_SUCCESS,
      actionTypes.CREATE_HOUSEHOLD_ERROR,
  )
}

function* getPeriodicity() {
  try {
    const response: { status: number; data: { data: {} } } =
      yield householdsAPI.getPeriodicity()

    if (response.status === 200) {
      yield updateToken()
      yield put(householdsActions.getPeriodicityAC(response.data))
    }
  } catch (error) {
    console.log(error)
  }
}

function* deleteHousehold({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "delete",
    endpoint: `/api/auth/households/${action.payload.data}`
  })

  yield dispatchResponse(
      { data, error },
      actionTypes.DELETE_HOUSEHOLD_SUCCESS,
      actionTypes.DELETE_HOUSEHOLD_ERROR,
  )
}

function* getHouseholdsForMap({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "get",
    endpoint: `/api/auth/households-for-map/`,
    params: {
      ...action.payload.data
    }
  })

  // mapGeneration / mergeMap travel beside the query (not in it) and reach the reducer
  // as meta — see GET_HOUSEHOLDS_TRIGGER_FOR_MAP_SUCCESS in householdsReducer.
  yield dispatchResponse(
      { data, error },
      actionTypes.GET_HOUSEHOLDS_TRIGGER_FOR_MAP_SUCCESS,
      actionTypes.GET_HOUSEHOLDS_TRIGGER_FOR_MAP_ERROR,
      {
        ...(action.meta || {}),
        mapGeneration: action.payload.mapGeneration,
        mergeMap: action.payload.mergeMap === true,
      },
  );
}

export function* householdsWatcher() {
  yield takeEvery(actionTypes.GET_HOUSEHOLDS_TRIGGER_FOR_MAP, SagaWrapper.withRequest(getHouseholdsForMap))
  yield takeEvery(actionTypes.GET_HOUSEHOLDS_TRIGGER, getHouseholds)
  yield takeEvery(actionTypes.GET_HOUSEHOLDS_FOR_DOWNLOAD_TRIGGER, getHouseholdsDownload)
  yield takeEvery(actionTypes.GET_HOUSEHOLD_TRIGGER, getHousehold)
  yield takeEvery(actionTypes.CREATE_HOUSEHOLD, SagaWrapper.withRequest(createHousehold))
  yield takeEvery(actionTypes.UPDATE_HOUSEHOLD_TRIGGER, SagaWrapper.withRequest(updateHousehold))
  yield takeEvery(actionTypes.GET_PERIODICITY_TRIGGER, getPeriodicity)
  yield takeEvery(actionTypes.DELETE_HOUSEHOLD_TRIGGER, SagaWrapper.withRequest(deleteHousehold))
  yield takeEvery(actionTypes.EXPORT_HOUSEHOLDS_TRIGGER, SagaWrapper.withRequest(exportHouseholds))
  yield takeEvery(actionTypes.IMPORT_HOUSEHOLDS_TRIGGER, SagaWrapper.withRequest(importHouseholds))
  yield takeEvery(actionTypes.CREATE_REGULAR_CATEGORY, createRegularCategory)
  yield takeEvery(actionTypes.DELETE_REGULAR_CATEGORY, deleteRegularCategory)
  yield takeEvery(actionTypes.UPDATE_REGULAR_CATEGORY, updateRegularCategory)
  yield takeEvery(actionTypes.GET_HOUSEHOLDS_CATEGORIES_TRIGGER, getHouseholdCategories)
  yield takeEvery(actionTypes.DELETE_HOUSEHOLDS_BY_IDS, SagaWrapper.withRequest(deleteHouseholdsByIds))
  yield takeEvery(actionTypes.DELETE_ALL_HOUSEHOLDS_EXCLUDE_IDS, SagaWrapper.withRequest(deleteAllHouseholdsExcludeIds))
  yield takeEvery(actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS, SagaWrapper.withRequest(removeRegularCategoriesForHouseholdsByIds))
  yield takeEvery(actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_HOUSEHOLDS_BY_IDS, SagaWrapper.withRequest(updateRegularCategoriesForHouseholdsByIds))
  yield takeEvery(actionTypes.UPDATE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS, SagaWrapper.withRequest(updateRegularCategoriesForAllHouseholdsExcludeIds))
  yield takeEvery(actionTypes.REMOVE_REGULAR_CATEGORIES_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS, SagaWrapper.withRequest(removeRegularCategoriesForAllHouseholdsExcludeIds))
  yield takeEvery(actionTypes.UPDATE_SCHEDULING_FOR_HOUSEHOLDS_BY_IDS, SagaWrapper.withRequest(updateSchedulingForHouseholdsByIds))
  yield takeEvery(actionTypes.UPDATE_SCHEDULING_FOR_ALL_HOUSEHOLDS_EXCLUDE_IDS, SagaWrapper.withRequest(updateSchedulingForAllHouseholdsExcludeIds))
  yield takeLatest(actionTypes.IMPORT_PREVIEW_TRIGGER, importPreview)
}